import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Screen = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1.75rem;
  background: var(--bg);
`;

const Mark = styled.div`
  position: relative;
  width: 76px;
  height: 76px;
  display: grid;
  place-items: center;

  /* spinning gradient ring */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: conic-gradient(from 0deg, #6366f1, #a855f7, #ec4899, #6366f1);
    -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 4px), #000 0);
    mask: radial-gradient(farthest-side, transparent calc(100% - 4px), #000 0);
    animation: ${spin} 1s linear infinite;
  }

  .initials {
    font-family: var(--font-heading);
    font-weight: 700;
    font-size: 1.5rem;
    background: var(--gradient-text);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

const Term = styled.div`
  font-family: Consolas, 'Courier New', monospace;
  font-size: 0.85rem;
  line-height: 1.9;
  color: var(--text-muted);
  min-height: ${(p) => p.$lines * 1.9}em;
  text-align: left;

  .prompt { color: var(--primary-light); }
  .ok { color: #34d399; }
  .cursor {
    display: inline-block;
    width: 0.55em;
    height: 1.05em;
    margin-left: 2px;
    vertical-align: text-bottom;
    background: var(--primary-light);
    animation: blink 0.9s steps(1) infinite;
  }
  @keyframes blink { 50% { opacity: 0; } }
`;

const LINES = [
  { text: 'initializing simon.dev', ok: false },
  { text: 'loading react + spring boot + coffee', ok: true },
  { text: 'compiling experience', ok: true },
  { text: 'ready — bienvenue !', ok: false },
];

const STEP_MS = 380;

const Preloader = ({ onLoaded }) => {
  const [visible, setVisible] = useState(true);
  const [shown, setShown] = useState(1);

  useEffect(() => {
    const stepper = setInterval(
      () => setShown((n) => Math.min(n + 1, LINES.length)),
      STEP_MS
    );
    const timer = setTimeout(() => {
      setVisible(false);
      if (onLoaded) setTimeout(onLoaded, 600);
    }, 1800);
    return () => {
      clearInterval(stepper);
      clearTimeout(timer);
    };
  }, [onLoaded]);

  return (
    <AnimatePresence>
      {visible && (
        <Screen
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6, ease: 'easeInOut' }}
        >
          <Mark><span className="initials">SS</span></Mark>
          <Term $lines={LINES.length} aria-label="Loading">
            {LINES.slice(0, shown).map((l, i) => (
              <div key={l.text}>
                <span className="prompt">&gt;</span> {l.text}
                {l.ok && '... '}
                {l.ok && <span className="ok">✓</span>}
                {i === shown - 1 && <span className="cursor" />}
              </div>
            ))}
          </Term>
        </Screen>
      )}
    </AnimatePresence>
  );
};

export default Preloader;
